import { getAttributeRecord, getAttributeString } from "@/lib/utils/dynamodb";
import { ReportData, StructureDefinition, FieldDefinition } from "./types";

// デフォルトの構造定義
export const DEFAULT_STRUCTURE_DEFINITION: StructureDefinition = {
  meeting_purpose: {
    type: "str",
    description: "商談目的"
  },
  hearing_contents: {
    type: "str", 
    description: "ヒアリング内容"
  },
  proposal: {
    type: "str",
    description: "提案内容"
  },
  cost: {
    type: "str",
    description: "費用提示"
  },
  reaction: {
    type: "str",
    description: "顧客反応"
  },
  next_actions: {
    type: "str",
    description: "次にやること"
  },
  other: {
    type: "str",
    description: "その他"
  }
};

// バックエンドから送られてくる実際のデータ構造に対応した構造定義
export const BACKEND_COMPATIBLE_STRUCTURE: StructureDefinition = {
  title: {
    type: "str",
    description: "タイトル"
  },
  date: {
    type: "str",
    description: "日時"
  },
  participants: {
    type: "str",
    description: "参加者"
  },
  main_points: {
    type: "str",
    description: "主要ポイント"
  },
  action_items: {
    type: "str",
    description: "アクションアイテム"
  }
};

// データに含まれるフィールドに基づいて適切な構造定義を選択する関数
export const detectStructureDefinition = (data: Record<string, unknown>): StructureDefinition => {
  // structure_definition_idがある場合は自動検出を行わない
  if (data?.structure_definition_id) {
    if (process.env.NODE_ENV === 'development') {
      console.log("detectStructureDefinition: structure_definition_idが存在するため自動検出をスキップ");
    }
    return DEFAULT_STRUCTURE_DEFINITION;
  }

  const dataKeys = Object.keys(data);
  
  // バックエンド互換フィールドが含まれているかチェック
  const backendFields = ['title', 'main_points', 'action_items'];
  const hasBackendFields = backendFields.some(field => dataKeys.includes(field));
  
  if (hasBackendFields) {
    if (process.env.NODE_ENV === 'development') {
      console.log("detectStructureDefinition: バックエンド互換構造を検出");
    }
    return BACKEND_COMPATIBLE_STRUCTURE;
  }
  
  if (process.env.NODE_ENV === 'development') {
    console.log("detectStructureDefinition: デフォルト構造を使用");
  }
  return DEFAULT_STRUCTURE_DEFINITION;
};

// 構造定義を解析する関数
export const parseStructureDefinition = (structureJson: string | StructureDefinition): StructureDefinition => {
  try {
    if (typeof structureJson === 'string') {
      return JSON.parse(structureJson);
    }
    return structureJson;
  } catch (error) {
    console.error("構造定義の解析に失敗:", error);
    return DEFAULT_STRUCTURE_DEFINITION;
  }
};

// 構造定義IDを取得する関数
export const getStructureDefinitionId = (data: ReportData): string | null => {
  // 💡 MySQL版：reportからformatidを取得（最優先）
  const report = data?.report;

  if (report?.formatid) {
    const formatId = getAttributeString(report.formatid);
    if (formatId) {
      console.log('getStructureDefinitionId: formatidから取得:', formatId);
      return formatId;
    }
  }

  if (report?.structure_definition_id) {
    const structureId = getAttributeString(report.structure_definition_id);
    if (structureId) {
      console.log('getStructureDefinitionId: structure_definition_idから取得:', structureId);
      return structureId;
    }
  }
  
  if (data?.structure_definition_id) {
    console.log('getStructureDefinitionId: トップレベルから取得:', data.structure_definition_id);
    return data.structure_definition_id;
  }
  
  console.log('getStructureDefinitionId: IDが見つかりませんでした', {
    hasReport: !!data?.report,
    reportKeys: data?.report ? Object.keys(data.report) : [],
    hasFormatId: !!data?.report?.formatid,
    hasStructureDefinitionId: !!data?.report?.structure_definition_id
  });
  
  // 詳細なデバッグ情報（文字列として出力）
  console.log('getStructureDefinitionId: reportKeys詳細:', JSON.stringify(data?.report ? Object.keys(data.report) : []));
  console.log('getStructureDefinitionId: formatid値詳細:', JSON.stringify(data?.report?.formatid));
  console.log('getStructureDefinitionId: レポート全体:', JSON.stringify(data?.report, null, 2));
  
  return null;
};

// ProcessingStatus用の構造定義ID取得関数
export const getStructureDefinitionIdFromProcessingStatus = (processingStatus: unknown): string | null => {
  // ProcessingStatusからReportDataに変換してgetStructureDefinitionIdを呼び出す
  const processingStat = processingStatus as any;
  const reportData: ReportData = {
    report: processingStat?.report,
    structure_definition_id: processingStat?.structure_definition_id
  };
  
  return getStructureDefinitionId(reportData);
};

// フィールドが配列型かどうかを判定する関数
export const isArrayField = (fieldDef: FieldDefinition): boolean => {
  return fieldDef.type.toLowerCase().includes('list') || 
         fieldDef.type.toLowerCase().includes('array');
};

// フィールドの表示順序を決定する関数
export const getFieldOrder = (structureDef: StructureDefinition): string[] => {
  // 全体要約を最初に表示
  const summaryFields = ['全体要約', 'summary', 'meeting_summary', 'overview'];
  
  // 基本情報フィールド
  const basicFields = ['meeting_purpose', 'client', 'datetime'];
  
  const allFields = Object.keys(structureDef);
  
  // 全体要約フィールドを最初に配置
  const summaryExisting = summaryFields.filter(field => allFields.includes(field));
  
  // 基本情報フィールドを配置
  const basicExisting = basicFields.filter(field => allFields.includes(field));
  
  // 残りのフィールド（参加者を含む）
  const remainingFields = allFields.filter(field => 
    !summaryFields.includes(field) && 
    !basicFields.includes(field)
  );
  
  return [...summaryExisting, ...basicExisting, ...remainingFields];
};

// 文字起こしデータを取得する関数
export const getTranscribedText = (data: ReportData): string => {
  if (process.env.NODE_ENV === 'development') {
    console.log("getTranscribedText: data structure", {
      hasReport: !!data?.report,
      reportKeys: data?.report ? Object.keys(data.report) : [],
      transcribedTextType: typeof data?.report?.transcribedText,
      transcribedTextValue: data?.report?.transcribedText,
      hasTranscribedText: !!data?.report?.transcribedText,
      // 小文字のフィールド名も確認
      hasTranscribedtext: !!data?.report?.transcribedtext,
      transcribedtextType: typeof data?.report?.transcribedtext,
      transcribedtextValue: data?.report?.transcribedtext
    });
  }
  
  // 大文字小文字の両方をチェック
  const transcribedText = data?.report?.transcribedText ?? data?.report?.transcribedtext;
  
  if (!transcribedText) {
    if (process.env.NODE_ENV === 'development') {
      console.log("getTranscribedText: no transcribedText found");
    }
    return '';
  }
  
  const finalText = getAttributeString(transcribedText);
  
  if (process.env.NODE_ENV === 'development') {
    console.log("getTranscribedText: extracted text length", finalText.length);
    console.log("getTranscribedText: extracted text preview", finalText.substring(0, 100) + "...");
  }
  
  return finalText;
};

// タイムスタンプを除去した純粋なテキストを取得する関数
export const getCleanTranscribedText = (data: ReportData): string => {
  const rawText = getTranscribedText(data);
  if (!rawText) return '';
  
  // タイムスタンプパターンを除去: [00:00], [01:23] 等
  return rawText
    .replace(/\[\d{2}:\d{2}\]/g, '') // タイムスタンプを除去
    .replace(/\s+/g, ' ') // 複数の空白を単一に
    .trim(); // 前後の空白を除去
};

// フォーマット関数
export const formatDate = (dateString: string) => {
  if (!dateString) return "不明";
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  } catch (e: unknown) {
    console.error("日時フォーマットエラー:", e);
    return dateString; // パースに失敗した場合は元の文字列を返す
  }
}; 

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log('📋 クリップボードコピー開始:', text.length, '文字');
    }
    
    if (!navigator.clipboard) {
      console.error('❌ navigator.clipboard がサポートされていません');
      // フォールバック: document.execCommand を使用
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        const success = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (process.env.NODE_ENV === 'development') {
          console.log('📋 フォールバック方式でコピー:', success ? '成功' : '失敗');
        }
        return success;
      } catch (fallbackError) {
        console.error('❌ フォールバック方式も失敗:', fallbackError);
        return false;
      }
    }
    
    await navigator.clipboard.writeText(text);
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ クリップボードコピー成功');
    }
    return true;
  } catch (e: unknown) {
    console.error("❌ クリップボードコピーエラー:", e);
    
    // エラーの種類に応じたメッセージを出力
    if (e instanceof Error) {
      if (e.name === 'NotAllowedError') {
        console.error('❌ クリップボードアクセスが拒否されました。ブラウザの設定を確認してください。');
      } else if (e.name === 'SecurityError') {
        console.error('❌ セキュリティエラー: HTTPSまたはlocalhostでのみクリップボードアクセスが可能です。');
      } else {
        console.error('❌ 不明なエラー:', e.message);
      }
    }
    
    return false;
  }
};
